/**
 * App.js - Main Application Component
 * Red Setter Homes Portal
 * Handles authentication and role-based routing
 */

import * as Sentry from "@sentry/react";
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";

// Import component styles
import "./App.css";
import "./components/ErrorBoundary.css";
import "./components/SelectionModals.css";

// Import page styles
import "./pages/SignIn.css";

// Import Portal components
import AdminPortal from "./portals/AdminPortal";
import ClientPortal from "./portals/ClientPortal";
import SignIn from "./pages/SignIn";

// Import global components
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";

// Loading component
const LoadingScreen = () => (
  <div className="loading-container">
    <div className="spinner"></div>
    <h2>Launching Red Setter Homes Portal...</h2>
  </div>
);

// Error component
const AccessDenied = () => (
  <div className="app-error">
    <div className="app-error-header">
      <h1>Access Denied</h1>
    </div>
    <div className="app-error-text">
      <p>
        Your email is not authorized to access this portal. Please contact{" "}
        <a href="mailto:info@redsetterhomes.com">Red Setter Homes</a> for
        assistance.
      </p>
    </div>
    <button onClick={() => auth.signOut()} className="btn-primary">
      Sign Out
    </button>
  </div>
);

/**
 * Protected Route Component
 * Ensures user is authenticated and has correct role
 */
const ProtectedRoute = ({ children, requiredRole, userRole }) => {
  if (!userRole) {
    return <Navigate to="/signin" replace />;
  }

  if (requiredRole && userRole !== requiredRole) {
    // Redirect to appropriate dashboard based on role
    if (userRole === "team") {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (userRole === "client") {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <AccessDenied />;
    }
  }

  return children;
};

/**
 * Retry delays (ms) for the getUserRole callable.
 *
 * Tuned to Cloud Run cold starts, NOT to fast transient blips. When no
 * warm instance exists, Cloud Run aborts the request outright (no HTTP
 * status) while a container boots — a boot measured at ~16s end to end
 * (15:02:49 abort -> 15:03:05 startup probe success).
 *
 * The schedule must therefore span past that boot, not inside it.
 * Attempts land at:
 *
 *   attempt 1  t=0s    (triggers the boot; aborted)
 *   attempt 2  t=2s    (still booting; likely aborted)
 *   attempt 3  t=8s    (still booting; may be aborted)
 *   attempt 4  t=26s   (container up ~16s — lands on a warm instance)
 *
 * The final 18s delay is the one that actually closes the gap: without
 * it every attempt falls inside the same cold window and a user with
 * no cached role still bounces to /signin. Worst case a user waits
 * ~26s before the last attempt starts, well inside ROLE_CALL_TIMEOUT_MS.
 *
 * @constant {Array<number>}
 */
const ROLE_RETRY_DELAYS_MS = [2000, 6000, 18000];

/**
 * Per-attempt timeout (ms) for the getUserRole callable.
 *
 * Comfortably longer than a worst-case cold start plus the Airtable
 * lookup, so a retry can land on the instance that is finishing its
 * boot rather than timing out alongside it.
 *
 * @constant {number}
 */
const ROLE_CALL_TIMEOUT_MS = 70000;

/**
 * Error codes worth retrying. These indicate the invocation never
 * reached (or could not be completed by) the handler. Codes such as
 * unauthenticated, invalid-argument, permission-denied and
 * failed-precondition are deterministic — retrying only delays the
 * user's feedback.
 *
 * @constant {Array<string>}
 */
const RETRYABLE_ROLE_ERROR_CODES = [
  "functions/internal",
  "functions/unavailable",
  "functions/deadline-exceeded",
  "internal",
  "unavailable",
  "deadline-exceeded",
];

/**
 * Whether a getUserRole failure should be retried.
 *
 * @param {Error} error - Error thrown by the httpsCallable invocation
 * @return {boolean} True if the call is worth retrying
 */
const isRetryableRoleError = (error) => {
  // A Cloud Run abort surfaces with no code at all — precisely the
  // cold-start case this retry exists for.
  if (!error?.code) return true;
  return RETRYABLE_ROLE_ERROR_CODES.includes(error.code);
};

/**
 * Pause for a given number of milliseconds.
 *
 * @param {number} ms - Delay in milliseconds
 * @return {Promise<void>} Resolves after the delay
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Main App Component
 */
function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check user role from Airtable.
   *
   * Retries on cold-start aborts and transient backend errors (see
   * ROLE_RETRY_DELAYS_MS). Deterministic failures fail fast.
   *
   * @param {Object} firebaseUser - The signed-in Firebase user
   * @return {Promise<string|null>} The resolved role, "unknown" if the
   *   user is not authorized, or null if the check could not complete
   */
  const checkUserRole = async (firebaseUser) => {
    try {
      console.log("Checking user role from Airtable for:", firebaseUser.email);
      setRoleLoading(true);

      const getUserRole = httpsCallable(functions, "getUserRole", {
        timeout: ROLE_CALL_TIMEOUT_MS,
      });

      let result;
      for (let attempt = 0; ; attempt += 1) {
        try {
          result = await getUserRole({ email: firebaseUser.email });
          break;
        } catch (callError) {
          const canRetry =
            attempt < ROLE_RETRY_DELAYS_MS.length &&
            isRetryableRoleError(callError);

          if (!canRetry) throw callError;

          const waitMs = ROLE_RETRY_DELAYS_MS[attempt];
          console.warn(
            `getUserRole attempt ${attempt + 1} failed ` +
              `(${callError?.code || "no status"}); ` +
              `retrying in ${waitMs}ms`,
            callError,
          );
          await delay(waitMs);
        }
      }

      console.log("getUserRole result:", result.data);

      if (result.data.success) {
        const { role, user } = result.data;

        console.log("User role set to:", role);
        console.log("User data set to:", user);
        setUserRole(role);
        setUserData(user);

        // ✅ Store in localStorage with safety checks
        if (role && role !== "undefined") {
          localStorage.setItem("userRole", role);
        } else {
          localStorage.removeItem("userRole");
        }

        if (user && user !== "undefined") {
          localStorage.setItem("userData", JSON.stringify(user));
        } else {
          localStorage.removeItem("userData");
        }

        // ✅ Set Sentry user context
        Sentry.setUser({
          email: firebaseUser.email,
          id: firebaseUser.uid,
          role: role,
          username: user?.name || firebaseUser.displayName,
        });

        return role;
      } else {
        console.error("Role check failed:", result.data.message);
        setError("Your email is not authorized.");
        setUserRole("unknown");
        return "unknown";
      }
    } catch (error) {
      console.error("Error checking user role:", error);
      Sentry.captureException(error, {
        tags: {
          errorCode: error?.code || "no-status",
          retriesExhausted: true,
        },
        extra: {
          email: firebaseUser.email,
          note: "getUserRole failed after all retries",
        },
      });

      // Degrade gracefully: if a cached role is already applied, the
      // user keeps working and does not need an alarming banner. Only
      // surface an error when there is no role to fall back on, since
      // that path otherwise dead-ends at the sign-in redirect.
      const hasCachedRole = Boolean(localStorage.getItem("userRole"));
      if (!hasCachedRole) {
        setError("Unable to verify access. Please try again.");
      } else {
        console.warn(
          "Role verification failed; continuing with cached role.",
        );
      }
      return null;
    } finally {
      setRoleLoading(false);
    }
  };

  /**
   * Monitor authentication state changes
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log(
        "Auth state changed:",
        firebaseUser ? firebaseUser.email : "signed out",
      );

      if (firebaseUser) {
        // User is signed in
        setUser(firebaseUser);

        // Check if we have cached role (for faster loading)
        const cachedRole = localStorage.getItem("userRole");
        const cachedUserData = localStorage.getItem("userData");

        // ✅ Check for valid data AND safe parsing
        if (cachedRole && cachedRole !== "undefined" && cachedRole !== "null") {
          console.log("Using cached role:", cachedRole);
          setUserRole(cachedRole);
        }

        if (
          cachedUserData &&
          cachedUserData !== "undefined" &&
          cachedUserData !== "null"
        ) {
          try {
            const parsedData = JSON.parse(cachedUserData);
            setUserData(parsedData);
          } catch (error) {
            console.error("Failed to parse cached userData:", error);
            localStorage.removeItem("userData"); // Clear corrupted data
          }
        }

        // Always verify role from server
        console.log("Verifying role from server...");
        await checkUserRole(firebaseUser);
      } else {
        // User is signed out
        console.log("User signed out, clearing state");
        setUser(null);
        setUserRole(null);
        setUserData(null);
        localStorage.removeItem("userRole");
        localStorage.removeItem("userData");
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  /**
   * Handle sign out
   */
  /* const handleSignOut = async () => {
    try {
      await auth.signOut();
      localStorage.clear();
      window.location.href = "/signin";
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };*/

  // Show loading screen while checking authentication
  if (loading) {
    return <LoadingScreen />;
  }

  // Show loading screen while checking user role
  if (roleLoading) {
    return <LoadingScreen />;
  }

  // Show error if user is not authorized
  if (error && userRole === "unknown") {
    return <AccessDenied />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <div className="app">
          <Routes>
            {/* Public Route - Sign In */}
            <Route
              path="/signin"
              element={
                user ? (
                  userRole === "team" ? (
                    <Navigate to="/admin/dashboard" replace />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                ) : (
                  <SignIn />
                )
              }
            />

            {/* Admin Portal - Single ProtectedRoute wrapper */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute requiredRole="team" userRole={userRole}>
                  <AdminPortal user={user} userData={userData} />
                </ProtectedRoute>
              }
            />

            {/* Client Portal - Single ProtectedRoute wrapper */}
            <Route
              path="/*"
              element={
                <ProtectedRoute requiredRole="client" userRole={userRole}>
                  <ClientPortal user={user} userData={userData} />
                </ProtectedRoute>
              }
            />

            {/* Root redirect */}
            <Route
              path="/"
              element={
                user ? (
                  userRole === "team" ? (
                    <Navigate to="/admin/dashboard" replace />
                  ) : userRole === "client" ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <Navigate to="/signin" replace />
                  )
                ) : (
                  <Navigate to="/signin" replace />
                )
              }
            />
          </Routes>
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
